import { useRef, useState } from 'react';
import { Color, ITEM_LOOKUP_REQUEST_PATH, LOOKUP_BUNDLES_PATH, LOOKUP_BUNDLE_MAX_ITEMS, Size, fill, t } from '@bms2/shared';
import { Button } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { downloadThroughFrame } from '../reports/api';
import { apiFetch, upload } from './api';
import { LookupStepThrough } from './LookupStepThrough';
import styles from './ItemLookupActs.module.css';

/** The ticked Items as the list resolves them: ids, or the sentence the selection route refused with. */
export type ResolvedSelection = { ok: true; ids: readonly string[] } | { ok: false; message: string };

/**
 * 🔒 Export Lookup Request on the Items list (piece 16, *Manufacturer lookup*;
 * decision 12660; T3814): the ticked Items saved as one JSON file naming each by
 * our id, its manufacturer and part number, the attributes it holds and the
 * fields still empty, for a person or an outside agent to fill as a lookup
 * bundle. A filter selection resolves through the list's own selection route
 * first, as Merge does (decision 7870). It writes nothing.
 */
export function ExportLookupRequestButton({ selectedCount, resolve, apiBaseUrl, onSaved, onRefused }: {
  selectedCount: number;
  resolve: () => Promise<ResolvedSelection>;
  apiBaseUrl: string;
  onSaved: (message: string) => void;
  onRefused: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const tooMany = selectedCount > LOOKUP_BUNDLE_MAX_ITEMS;
  const press = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const resolved = await resolve();
      if (!resolved.ok) return onRefused(resolved.message);
      const body = JSON.stringify({ itemIds: resolved.ids });
      const saved = await downloadThroughFrame((url) => apiFetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body }), `${apiBaseUrl}${ITEM_LOOKUP_REQUEST_PATH}`, 'lookup-request.json');
      if (saved) onSaved(t('itemLookup.request.saved'));
      else onRefused(t('itemLookup.request.failed'));
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  return (
    <span data-item-lookup-request="">
      <Button
        size={Size.Small}
        color={Color.Navy}
        text={[t('itemLookup.request.action')]}
        actions={[() => void press()]}
        disabled={selectedCount === 0 || tooMany || busy}
        {...(selectedCount === 0 ? { disabledReason: t('itemLookup.request.none') } : tooMany ? { disabledReason: fill(t('itemLookup.request.tooMany'), { max: LOOKUP_BUNDLE_MAX_ITEMS }) } : {})}
      />
    </span>
  );
}

/**
 * 🔒 Import Lookup Bundle on the Items list (piece 16, *Manufacturer lookup*;
 * decision 12661; T3814): the bundle read and staged as an import run, refused
 * by name where its manifest names an Item or a file it does not hold, then the
 * run's Step Through opens on its first Item. Nothing is written to an Item
 * until a step accepts it. Drawn for a writer of the Item module.
 */
export function ImportLookupBundleButton({ apiBaseUrl, onDone }: { apiBaseUrl: string; onDone: (wrote: boolean) => void }) {
  const [choosing, setChoosing] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const shut = () => {
    setChoosing(false);
    setFile(null);
    setRefusal(null);
  };
  const read = async () => {
    if (!file) return setRefusal(t('itemLookup.bundle.choose'));
    setBusy(true);
    setRefusal(null);
    const form = new FormData();
    form.append('file', file, file.name);
    const answer = await upload<{ runId: string }>(LOOKUP_BUNDLES_PATH, form, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setRefusal(answer.message);
    shut();
    setRunId(answer.data.runId);
  };
  return (
    <>
      <span data-item-lookup-bundle="">
        <Button size={Size.Small} text={[t('itemLookup.bundle.action')]} actions={[() => setChoosing(true)]} />
      </span>
      {/* Mounted only while open, so a second import starts from a clean picker. */}
      {choosing ? (
        <FormFrame
          host="window"
          open
          title={t('itemLookup.bundle.title')}
          onCancel={shut}
          dataForm="lookup-bundle"
          typed={file !== null}
          refusal={refusal}
          onDismissRefusal={() => setRefusal(null)}
          act={{ label: t('itemLookup.bundle.read'), onAct: () => void read(), disabled: file === null || busy, ...(file === null ? { disabledReason: t('itemLookup.bundle.choose') } : {}) }}
        >
          <label className={styles.picker}>
            <span className={styles.pickerLabel}>{t('itemLookup.bundle.file')}</span>
            <input type="file" accept=".zip,application/zip" data-lookup-bundle-file onChange={(event) => setFile(event.target.files?.[0] ?? null)} disabled={busy} />
            <span className={styles.hint}>{t('itemLookup.bundle.hint')}</span>
          </label>
        </FormFrame>
      ) : null}
      {/* 🔒 The run's own Step Through, keyed to the run so a second bundle never opens on the first one's steps. */}
      {runId ? <LookupStepThrough key={runId} runId={runId} apiBaseUrl={apiBaseUrl} onClosed={(wrote) => { setRunId(null); onDone(wrote); }} /> : null}
    </>
  );
}
